import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { LogOut, User, Settings, Shield, HelpCircle, Moon, Sun, Heart, Bell, Handshake, Send, Gift } from 'lucide-react';

interface UserMenuProps {
  role?: 'festival' | 'stand';
  promoterActive?: boolean;
}

export function UserMenu({ role, promoterActive }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logOut } = useAuth();
  const { theme: currentTheme, toggleTheme } = useTheme();
  const location = useLocation();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const handleLogout = async () => {
    try {
      if (logOut) {
        await logOut();
      }
      navigate('/');
    } catch (error) {
      console.error("Error logging out", error);
    }
  };

  const currentRole = role || location.state?.role || 'festival';

  return (
    <div className="relative ml-2" ref={dropdownRef}>
      <div 
        className="flex items-center cursor-pointer pl-2 border-l border-white/10 h-8"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#4b3cfa] to-[#ff5a00] overflow-hidden flex items-center justify-center text-xs text-white font-bold transition-transform hover:scale-105">
          {user?.email?.charAt(0).toUpperCase() || 'U'}
        </div>
      </div>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-[#1A1A1A] border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden text-left origin-top-right">
          <div className="p-4 border-b border-white/5 bg-[#121212]">
            <p className="font-bold text-white truncate">{user?.email}</p>
            <p className="text-xs text-gray-500 mt-1 capitalize">{currentRole === 'stand' ? t('nav.roleStand') : t('nav.roleFestival')}</p>
          </div>
          
          <div className="p-2 space-y-1">
            <button 
              onClick={() => {
                setIsOpen(false);
                navigate(currentRole === 'stand' ? '/stand/profile' : '/my-festivals');
              }}
              className="w-full flex items-center text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
            >
              <User className="w-4 h-4 mr-3 text-gray-400" />
              {t('userMenu.mySpace')}
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/how-it-works');
              }}
              className="w-full flex items-center text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
            >
              <HelpCircle className="w-4 h-4 mr-3 text-gray-400" />
              {t('userMenu.howItWorks')}
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications');
              }}
              className="w-full flex items-center text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
            >
              <Bell className="w-4 h-4 mr-3 text-gray-400" />
              {t('userMenu.notifications')}
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/home', { state: { role: currentRole, activeFilter: 'Favoris' } });
              }}
              className="w-full flex items-center text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
            >
              <Heart className="w-4 h-4 mr-3 text-gray-400" />
              {t('userMenu.myFavorites')}
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/settings');
              }}
              className="w-full flex items-center text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
            >
              <Settings className="w-4 h-4 mr-3 text-gray-400" />
              {t('userMenu.settings')}
            </button>
            {currentRole === 'festival' && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/explore-stands');
                }}
                className="w-full flex items-center text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
              >
                <Shield className="w-4 h-4 mr-3 text-gray-400" />
                {t('nav.exploreStands')}
              </button>
            )}
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/support');
              }}
              className="w-full flex items-center text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
            >
              <HelpCircle className="w-4 h-4 mr-3 text-gray-400" />
              {t('userMenu.helpSupport')}
            </button>
            {currentRole === 'stand' && (
              <button
                onClick={() => { setIsOpen(false); navigate('/stand/parrainage'); }}
                className="w-full flex items-center text-left px-3 py-2 text-sm text-[#FF5A00] hover:bg-[#FF5A00]/10 rounded-xl transition-colors font-semibold"
              >
                <Gift className="w-4 h-4 mr-3" />
                {t('userMenu.referExhibitor')}
              </button>
            )}
            {/* Affiliation : soumettre un festival si promoteur actif, sinon devenir promoteur */}
            {promoterActive ? (
              <button
                onClick={() => { setIsOpen(false); navigate('/promoter/submit-festival'); }}
                className="w-full flex items-center text-left px-3 py-2 text-sm text-[#FF5A00] hover:bg-[#FF5A00]/10 rounded-xl transition-colors font-semibold"
              >
                <Send className="w-4 h-4 mr-3" />
                {t('nav.submitFestival')}
              </button>
            ) : (
              <button
                onClick={() => { setIsOpen(false); navigate('/become-promoter'); }}
                className="w-full flex items-center text-left px-3 py-2 text-sm text-[#FF5A00] hover:bg-[#FF5A00]/10 rounded-xl transition-colors font-semibold"
              >
                <Handshake className="w-4 h-4 mr-3" />
                {t('nav.becomePromoter')}
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleTheme();
              }}
              className="w-full flex items-center justify-between text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
            >
              <div className="flex items-center">
                {currentTheme === 'light' ? <Sun className="w-4 h-4 mr-3 text-gray-400" /> : <Moon className="w-4 h-4 mr-3 text-gray-400" />}
                {t('userMenu.theme')}
              </div>
              <span className="text-xs text-gray-500 font-medium bg-black/50 px-2 py-0.5 rounded cursor-pointer border border-white/5">
                {currentTheme === 'light' ? t('userMenu.light') : t('userMenu.dark')}
              </span>
            </button>
          </div>

          <div className="p-2 border-t border-white/5 bg-[#121212]">
            <button
              onClick={handleLogout}
              className="w-full flex items-center text-left px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 rounded-xl transition-colors font-semibold"
            >
              <LogOut className="w-4 h-4 mr-3" />
              {t('userMenu.logout')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
